import { HomePageContent, ModernHomePageContent } from "@app/home/pages";
import { resolveHomeExperienceResponse } from "@app/home/utils/resolve-home-experience-response";
import type { TrainingGroundHomeData } from "@app/home/utils/training-ground-home";
import { getLeagueSummaryService, type LeagueSummary } from "@core/league";
import { MaintenanceContent } from "@app/shared/components";
import { getHomeExperienceService } from "@core/home/services";
import {
  type HomeExperienceResponse,
  type HomeResponse,
} from "@core/home/types";
import { useLogError } from "@core/shared/hooks";
import { ErrorLogData, logErrorService } from "@core/shared/services";
import { URLS } from "@core/shared/utils";
import { userAtom } from "@framework/config/atoms/user-atom";
import { getRootSettings, pageHead } from "@framework/config/drivers/meta";
import {
  getSafeErrorDiagnostic,
  getSafeIdentityDiagnostic,
  getSafeRequestDiagnostic,
  getSafeSettingsHost,
  type HomeSsrRootDiagnostic,
  logHomeSsrDiagnostic,
} from "@framework/config/utils/home-ssr-diagnostics";
import { settingsAtom } from "@framework/shared/atoms/settings.atom";
import { trans } from "@mongez/localization";
import { MetaFunction } from "@remix-run/node";
import {
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { useEffect } from "react";
import { publicRoutes } from "../middleware";

type HomeRouteExperience = HomeExperienceResponse & {
  trainingGroundHome?: TrainingGroundHomeData;
  /** League v2 `?view=card` summary (students only, fail-soft). */
  leagueHome?: LeagueSummary;
};

export const loader = publicRoutes(
  async ({
    http,
    user,
    success,
    queryString,
    userAgent,
    badRequest,
    removeUser,
    redirectTo,
    request,
    requestId,
    diagnosticContext,
    domain,
  }) => {
    logHomeSsrDiagnostic(diagnosticContext, "home_loader_started", {
      resolvedDomain: domain,
      ...getSafeIdentityDiagnostic(user),
    });

    const studentId = Number(user.id);
    const shouldLoadTrainingGround =
      user.userType === "user" &&
      user.isStudent === true &&
      Number.isSafeInteger(studentId) &&
      studentId > 0;
    const optionalTrainingGroundRequest = (path: string) =>
      shouldLoadTrainingGround
        ? http.get(path).catch(() => null)
        : Promise.resolve(null);

    try {
      // Keep Shorts independent from the home payload so a disabled or
      // unavailable Shorts endpoint cannot prevent the home page rendering.
      const [
        experienceResponse,
        shortsResponse,
        trainingGroundConfigurationResponse,
        trainingGroundMistakesResponse,
        trainingGroundAnalyticsResponse,
        leagueHome,
      ] = await Promise.all([
        getHomeExperienceService(http, queryString.all()),
        http.get("/shorts", { params: { limit: 12 } }).catch(() => null),
        optionalTrainingGroundRequest("/training-ground/configuration"),
        optionalTrainingGroundRequest("/training-ground/mistakes"),
        optionalTrainingGroundRequest("/training-ground/analytics"),
        // One cheap card read on every student home open; a failure (or a
        // 403 while League is off) simply hides the card.
        shouldLoadTrainingGround
          ? getLeagueSummaryService(http, { card: true }).catch(() => null)
          : Promise.resolve(null),
      ]);

      const response = resolveHomeExperienceResponse(experienceResponse);

      // An empty experience is expected (maintenance/fallback), not an error.
      if (!response) {
        logHomeSsrDiagnostic(diagnosticContext, "home_loader_empty_experience", {
          resolvedDomain: domain,
          ...getSafeIdentityDiagnostic(user),
        });

        return success({ experience: undefined });
      }

      const trainingGroundAvailable =
        trainingGroundConfigurationResponse?.data?.configuration?.enabled ===
          true &&
        Array.isArray(trainingGroundConfigurationResponse?.data?.catalog) &&
        trainingGroundConfigurationResponse.data.catalog.length > 0;

      const experience = {
        ...response,
        trainingGroundHome: trainingGroundAvailable
          ? {
              studentId,
              mistakes: trainingGroundMistakesResponse?.data?.mistakes,
              analytics: trainingGroundAnalyticsResponse?.data?.analytics,
            }
          : undefined,
        leagueHome: leagueHome?.enabled ? leagueHome : undefined,
        data: {
          ...response.data,
          shorts: shortsResponse?.data?.shorts ?? [],
        },
      } as HomeRouteExperience;

      logHomeSsrDiagnostic(diagnosticContext, "home_loader_succeeded", {
        resolvedDomain: domain,
        variant: experience.variant,
        hasData: Boolean(experience.data),
        shortsRequestSucceeded: Boolean(shortsResponse),
        shortsCount: Array.isArray(shortsResponse?.data?.shorts)
          ? shortsResponse.data.shorts.length
          : 0,
        ...getSafeIdentityDiagnostic(user),
      });

      // `success` intentionally unwraps a top-level `data` key for legacy
      // endpoint responses. Preserve the home-experience envelope here so
      // the route can still choose between the classic and social variants.
      return success({ experience });
    } catch (error: unknown) {
      const safeError = getSafeErrorDiagnostic(error);
      const safeIdentity = getSafeIdentityDiagnostic(user);
      const safeRequest = getSafeRequestDiagnostic(request);
      const message =
        typeof safeError.errorMessage === "string"
          ? safeError.errorMessage
          : "Home loader failed";

      logHomeSsrDiagnostic(
        diagnosticContext,
        "home_loader_failed",
        {
          resolvedDomain: domain,
          ...safeIdentity,
          ...safeError,
        },
        "error",
      );

      if (safeError.responseStatus === 401) {
        removeUser();
        return redirectTo(URLS.home);
      }

      const errorData: ErrorLogData = {
        message,
        stack:
          typeof safeError.errorStack === "string"
            ? safeError.errorStack
            : undefined,
        source: "home-response",
        url:
          typeof safeRequest.requestUrl === "string"
            ? safeRequest.requestUrl
            : "/",
        userAgent: userAgent.slice(0, 500),
        timestamp: Date.now(),
        userId:
          typeof safeIdentity.userId === "string" ||
          typeof safeIdentity.userId === "number"
            ? safeIdentity.userId
            : undefined,
        additionalData: {
          requestId,
          domain,
          errorName: safeError.errorName,
          errorCode: safeError.errorCode,
          responseStatus: safeError.responseStatus,
          requestMethod: safeError.requestMethod,
          requestPath: safeError.requestPath,
          userType: safeIdentity.userType,
          clientId: safeIdentity.clientId,
          clientType: safeIdentity.clientType,
        },
      };

      await logErrorService(http, errorData);

      return badRequest({
        error: message,
      });
    }
  },
);

export default function Index() {
  // Loader data can be null on the client (e.g. after a failed revalidation
  // or a manifest-mismatch reload), which crashed the destructuring.
  const { experience } =
    useLoaderData<{
      experience?: HomeRouteExperience;
    }>() ?? {};
  const rootData = useRouteLoaderData<{
    diagnostic?: HomeSsrRootDiagnostic;
  }>("root");
  const diagnostic = rootData?.diagnostic;

  if (typeof window === "undefined" && diagnostic) {
    const observedUser = getSafeIdentityDiagnostic(userAtom.value);
    const observedSettingsHost = getSafeSettingsHost(
      settingsAtom.value.general?.url,
    );

    logHomeSsrDiagnostic(diagnostic, "home_render_observed", {
      resolvedDomain: diagnostic.domain,
      expectedSettingsHost: diagnostic.settingsHost,
      observedSettingsHost,
      expectedUserId: diagnostic.user.userId,
      observedUserId: observedUser.userId,
      expectedUserType: diagnostic.user.userType,
      observedUserType: observedUser.userType,
      userIdentityMatches:
        diagnostic.user.userId === observedUser.userId &&
        diagnostic.user.userType === observedUser.userType,
      settingsHostMatches: diagnostic.settingsHost === observedSettingsHost,
      experienceVariant: experience?.variant ?? null,
      experienceHasData: Boolean(experience?.data),
    });
  }

  if (!experience?.data) {
    return (
      <MaintenanceContent
        type="general"
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (experience.variant === "classic") {
    return (
      <HomePageContent
        data={experience.data as HomeResponse}
        trainingGroundHome={experience.trainingGroundHome}
        leagueHome={experience.leagueHome}
      />
    );
  }

  return (
    <ModernHomePageContent
      data={experience.data as HomeResponse}
      trainingGroundHome={experience.trainingGroundHome}
      leagueHome={experience.leagueHome}
    />
  );
}

export function ErrorBoundary() {
  const error = useRouteError() as any;
  const { logError } = useLogError();
  const rootData = useRouteLoaderData<{
    diagnostic?: HomeSsrRootDiagnostic;
  }>("root");
  const diagnostic = rootData?.diagnostic;

  if (typeof window === "undefined" && diagnostic) {
    logHomeSsrDiagnostic(
      diagnostic,
      "home_error_boundary_rendered",
      {
        ...getSafeIdentityDiagnostic(userAtom.value),
        ...getSafeErrorDiagnostic(error),
      },
      "error",
    );
  }

  // Log route errors to backend
  useEffect(() => {
    // Expected 4xx route responses (unknown URLs, bot probes) are not bugs.
    if (isRouteErrorResponse(error) && error.status < 500) return;

    if (error) {
      let errorToLog: Error;

      if (error instanceof Error) {
        errorToLog = error;
      } else if (isRouteErrorResponse(error)) {
        errorToLog = new Error(
          `Route Error: ${error.status} ${error.statusText}`,
        );
        errorToLog.stack = error.data?.message || error.statusText;
      } else {
        errorToLog = new Error("Unknown route error");
        errorToLog.stack = JSON.stringify(error);
      }

      logError(errorToLog, {
        type: "routeError",
        routeError: true,
        errorData: error,
      });
    }
  }, [error, logError]);

  return (
    <div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center gap-5 py-10">
      <div className="flex flex-col items-center gap-3 text-center">
        <h1 className="text-4xl font-bold text-destructive">
          {trans("socialHome.loadErrorTitle")}
        </h1>
        <p className="text-muted-foreground">
          {trans("socialHome.loadErrorDescription")}
        </p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          {trans("retry")}
        </button>
        <button
          onClick={() => window.history.back()}
          className="rounded-md border border-input px-4 py-2 hover:bg-accent"
        >
          {trans("back")}
        </button>
      </div>
    </div>
  );
}

export const meta: MetaFunction = ({ matches }) => {
  const settings = getRootSettings(matches);

  return pageHead({
    title: settings.general?.appName || "",
    description: settings.general?.description || "",
    image: settings.general?.logo?.url || "",
    settings,
  });
};
